// src/pages/Mascotas.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import GlassCard from '../components/UI/GlassCard';
import Table from '../components/UI/Table';
import Modal from '../components/UI/Modal';
import { 
  Dog, 
  Search, 
  Edit2, 
  Trash2, 
  Plus, 
  ChevronRight,
  Calendar,
  Scale,
  User,
  Scissors,
  History,
  Clock,
  CheckCircle2,
  ArrowRight,
  Camera,
  Upload,
  X,
  FileText,
  Eye
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Swal from 'sweetalert2';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';

// Componente para evitar el warning de ref en React 19
const SafeTippy = ({ children, ...props }) => {
  const [ref, setRef] = useState(null);
  return (
    <>
      <Tippy {...props} reference={ref} />
      {React.cloneElement(React.Children.only(children), { ref: setRef })}
    </>
  );
};

const Mascotas = () => {
    const { can } = useAuth();
    const [mascotas, setMascotas] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [especies, setEspecies] = useState([]);
    const [razas, setRazas] = useState([]);
    const [filteredRazas, setFilteredRazas] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Modales
    const [showModal, setShowModal] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [selectedMascota, setSelectedMascota] = useState(null);
    const [historyData, setHistoryData] = useState({ servicios_realizados: [], agenda_pendiente: [] });
    const [loadingHistory, setLoadingHistory] = useState(false);
    
    // Form State
    const [editingMascota, setEditingMascota] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [formData, setFormData] = useState({
        cliente_id: '',
        nombre: '',
        especie_id: '1',
        genero: 'Macho',
        raza_id: '',
        raza_custom: '',
        fecha_nacimiento: '',
        peso: '',
        notas: '',
        foto_base64: null,
        foto_url: null
    });

    const fetchData = useCallback(async () => {
        try {
            const [mRes, cRes, eRes, rRes] = await Promise.all([
                axios.get(`/backend/api/mascotas/index.php`),
                axios.get('/backend/api/clientes/index.php'),
                axios.get('/backend/api/mascotas/especies.php'),
                axios.get('/backend/api/mascotas/razas.php')
            ]);
            
            if (mRes.data.success) setMascotas(mRes.data.data);
            if (cRes.data.success) setClientes(cRes.data.data);
            if (eRes.data.success) setEspecies(eRes.data.data);
            if (rRes.data.success) setRazas(rRes.data.data);
        } catch (error) {
            toast.error('Error al sincronizar datos');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (formData.especie_id) {
            const filtered = razas.filter(r => r.especie_id.toString() === formData.especie_id.toString());
            setFilteredRazas(filtered);
        } else {
            setFilteredRazas([]);
        }
    }, [formData.especie_id, razas]);

    const handleEdit = (m) => {
        setEditingMascota(m);
        setPhotoPreview(m.foto_url ? `/${m.foto_url}` : null);
        setFormData({
            cliente_id: m.cliente_id,
            nombre: m.nombre,
            especie_id: m.especie_id,
            genero: m.genero,
            raza_id: m.raza_id || '',
            raza_custom: m.raza_custom || '',
            fecha_nacimiento: m.fecha_nacimiento || '',
            peso: m.peso || '',
            notas: m.notas_grooming || '',
            foto_url: m.foto_url || null,
            foto_base64: null
        });
        setShowModal(true);
    };

    const handleViewHistory = async (m) => {
        setSelectedMascota(m);
        setLoadingHistory(true);
        setShowHistory(true);
        try {
            const response = await axios.get(`/backend/api/mascotas/historial.php?id=${m.id}`);
            if (response.data.success) {
                setHistoryData(response.data.data);
            }
        } catch (error) {
            toast.error('Error al cargar el expediente histórico');
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: '¿Retirar Mascota?',
            text: "Se eliminará el historial clínico vinculado a este perfil.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            confirmButtonText: 'Sí, eliminar',
            background: 'rgba(15, 23, 42, 0.95)',
            color: '#fff'
        });

        if (result.isConfirmed) {
            try {
                const response = await axios.delete(`/backend/api/mascotas/index.php?id=${id}`);
                if (response.data.success) {
                    toast.success('Mascota eliminada del sistema');
                    fetchData();
                }
            } catch (error) {
                toast.error('Error en el servidor');
            }
        }
    };

    const handleSave = async () => {
        if (!formData.nombre || !formData.cliente_id) {
            return toast.error('Complete los datos del propietario y mascota');
        }

        try {
            const url = '/backend/api/mascotas/index.php';
            const method = editingMascota ? 'PUT' : 'POST';
            const data = editingMascota ? { ...formData, id: editingMascota.id } : formData;

            const response = await axios({ method, url, data });

            if (response.data.success) {
                toast.success(editingMascota ? 'Ficha actualizada' : 'Mascota registrada');
                setShowModal(false);
                setEditingMascota(null);
                setPhotoPreview(null);
                fetchData();
            }
        } catch (error) {
            toast.error('Error al procesar registro');
        }
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                return toast.error('La imagen no debe exceder los 2MB');
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result);
                setFormData(prev => ({ ...prev, foto_base64: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const calcularEdadMascota = (fecha) => {
        if (!fecha) return 'Desconocida';
        const nac = new Date(fecha);
        const hoy = new Date();
        let edad = hoy.getFullYear() - nac.getFullYear();
        const m = hoy.getMonth() - nac.getMonth();
        if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad--;
        return edad > 0 ? `${edad} años` : 'Cachorro';
    };

    return (
        <div className="page-enter p-8 lg:p-12 max-w-[1600px] mx-auto min-h-screen text-white">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-6">
                <div>
                    <h1 className="text-5xl font-black flex items-center gap-4">
                        <Dog className="text-secondary" size={48} /> 
                        Catálogo de <span className="text-secondary italic font-medium">Mascotas</span>
                    </h1>
                    <p className="text-white/40 mt-2 text-sm lg:text-base font-medium">Fichas técnicas y expedientes veterinarios de grooming</p>
                </div>
                
                <div className="flex w-full md:w-auto gap-4">
                    <button 
                        onClick={() => {
                            const url = `/backend/api/reportes/mascotas_pdf.php?token=${localStorage.getItem('token')}`;
                            if ((window.innerWidth < 1024) || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
                                toast.success('Abriendo catálogo en visor nativo de la tablet... 🐕');
                                window.open(url, '_blank');
                            } else {
                                setShowPreview(true);
                            }
                        }}
                        className="glass hover:bg-white/10 flex items-center gap-3 px-6 py-3 rounded-2xl text-white font-bold text-xs uppercase tracking-widest transition-all active:scale-95 border border-white/5 shadow-xl shadow-black/20"
                    >
                        <Eye size={18} className="text-secondary" />
                        <span className="hidden sm:inline">Vista Previa Catálogo</span>
                    </button>

                    {can('Mascotas', 'crear') && (
                        <button 
                            onClick={() => { 
                                setEditingMascota(null); 
                                setPhotoPreview(null);
                                setFormData({
                                    cliente_id: '',
                                    nombre: '',
                                    especie_id: '1',
                                    genero: 'Macho',
                                    raza_id: '',
                                    raza_custom: '',
                                    fecha_nacimiento: '',
                                    peso: '',
                                    notas: '',
                                    foto_base64: null,
                                    foto_url: null
                                });
                                setShowModal(true); 
                            }}
                            className="premium-btn-primary flex items-center gap-3 px-8 shadow-xl shadow-primary/20"
                        >
                            <Plus size={20} />
                            <span className="hidden sm:inline">Nueva Mascota</span>
                        </button>
                    )}
                </div>
            </header>

            <div className="grid grid-cols-1 gap-8">
                <GlassCard padding="0" className="shadow-2xl border-white/5 overflow-hidden min-h-[600px]">
                    <Table 
                        headers={['Mascota', 'Propietario', 'Info Técnica', 'Peso', 'Estado']}
                        data={mascotas.map(m => ({
                            _raw: m,
                            mascota: (
                                <div className="flex items-center gap-6 py-4">
                                   <SafeTippy 
                                        content={m.foto_url ? (
                                            <div className="p-1 glass rounded-3xl overflow-hidden shadow-2xl border border-white/10 transition-all">
                                                <img src={`/${m.foto_url}`} alt={m.nombre} className="w-64 h-64 object-cover" />
                                                <div className="bg-slate-900/80 p-3 flex justify-between items-center">
                                                    <span className="font-black text-white">{m.nombre}</span>
                                                    <span className="text-[10px] font-bold text-white/40 uppercase tracking-widest">{m.raza || 'Raza Mixta'}</span>
                                                </div>
                                            </div>
                                        ) : null}
                                        placement="right"
                                        theme="glass-premium"
                                        animation="scale"
                                        disabled={!m.foto_url}
                                   >
                                       <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 border border-white/10 flex items-center justify-center text-2xl shadow-inner relative group cursor-pointer overflow-hidden transition-all hover:scale-110 active:scale-95">
                                          {m.foto_url ? (
                                              <img src={`/${m.foto_url}`} alt={m.nombre} className="w-full h-full object-cover" />
                                          ) : (
                                              m.genero === 'Hembra' ? '🎀' : '🐕'
                                          )}
                                          <div className="absolute inset-0 bg-secondary/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                       </div>
                                   </SafeTippy>
                                   <div className="flex flex-col">
                                      <span className="font-extrabold text-lg text-white tracking-tighter">{m.nombre}</span>
                                      <span className="text-[10px] text-white/30 uppercase font-black tracking-widest">{m.raza || m.raza_custom || 'Raza Mixta'}</span>
                                   </div>
                                </div>
                            ),
                            propietario: (
                                <div className="flex flex-col">
                                   <span className="font-bold text-white/90">{m.cliente_nombre} {m.cliente_apellido}</span>
                                   <span className="text-[10px] text-white/30 truncate flex items-center gap-1 uppercase tracking-widest"><User size={10}/> Socio Activo</span>
                                </div>
                            ),
                            info: (
                              <div className="flex items-center gap-4">
                                <div className="flex flex-col">
                                   <span className="text-[10px] uppercase font-bold tracking-widest">Edad</span>
                                   <span className="text-xs text-white/60 font-black">{calcularEdadMascota(m.fecha_nacimiento)}</span>
                                </div>
                              </div>
                            ),
                            peso: (
                              <div className="inline-flex items-center gap-2 bg-white/5 px-3 py-1 rounded-xl border border-white/5">
                                <Scale size={14} className="text-secondary" />
                                <span className="font-black text-sm">{m.peso} <span className="text-[10px] text-white/30">LBS</span></span>
                              </div>
                            ),
                            estado: <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1 rounded-full"><div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div><span className="text-[10px] text-emerald-400 font-extrabold uppercase tracking-widest">Saludable</span></div>
                        }))}
                        actions={(row) => (
                            <div className="flex gap-2">
                                <SafeTippy content="Ver Historial">
                                    <button onClick={() => handleViewHistory(row._raw)} className="glass hover:bg-secondary/10 p-2 rounded-xl text-secondary transition-all active:scale-95 group">
                                        <History size={16} />
                                    </button>
                                </SafeTippy>
                                {can('Mascotas', 'editar') && (
                                    <SafeTippy content="Editar Ficha Veterinaria">
                                        <button onClick={() => handleEdit(row._raw)} className="glass hover:bg-white/10 p-2 rounded-xl text-primary-light transition-all active:scale-95 group">
                                            <Edit2 size={16} />
                                        </button>
                                    </SafeTippy>
                                )}
                                {can('Mascotas', 'eliminar') && (
                                    <SafeTippy content="Retirar del Catálogo">
                                        <button onClick={() => handleDelete(row._raw.id)} className="glass hover:bg-red-500/10 p-2 rounded-xl text-red-400 transition-all active:scale-95 group">
                                            <Trash2 size={16} />
                                        </button>
                                    </SafeTippy>
                                )}
                            </div>
                        )}
                        emptyMessage="No se encontraron mascotas en el sistema"
                    />
                </GlassCard>
            </div>

            <Modal 
                show={showModal} 
                onClose={() => setShowModal(false)}
                title={editingMascota ? `Editando a ${formData.nombre}` : 'Ficha Veterinaria - Nueva Mascota'}
                size="lg"
                footer={(
                    <button 
                         onClick={handleSave} 
                         className="premium-btn-primary flex items-center gap-2 px-8"
                    >
                          <Dog size={18} />
                          {editingMascota ? 'Guardar Cambios' : 'Registrar Mascota'}
                    </button>
                )}
            >
                <form className="grid grid-cols-1 md:grid-cols-3 gap-8 pb-4">
                     <div className="md:col-span-3 pb-6 border-b border-white/5 mb-2">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-secondary flex items-center gap-2 mb-4">
                              <User size={12} /> Propietario Designado
                          </label>
                          <select 
                            value={formData.cliente_id} 
                            onChange={e => setFormData({...formData, cliente_id: e.target.value})} 
                            className="input-field bg-slate-800/80"
                            disabled={editingMascota}
                          >
                              <option value="" disabled>Selecciona al dueño responsable...</option>
                              {clientes.map(c => <option key={c.id} value={c.id} className="bg-slate-900">{c.nombre} {c.apellido} — {c.telefono}</option>)}
                          </select>
                     </div>

                           <div className="flex flex-col gap-3">
                                <input 
                                    type="file" 
                                    id="pet-photo" 
                                    accept="image/*" 
                                    capture="environment" 
                                    className="hidden" 
                                    onChange={handlePhotoChange}
                                />
                                <label 
                                    htmlFor="pet-photo"
                                    className="w-full aspect-square glass rounded-3xl flex flex-col items-center justify-center border-2 border-dashed border-white/10 hover:border-secondary/40 transition-all cursor-pointer group relative overflow-hidden shadow-2xl"
                                >
                                    {photoPreview ? (
                                        <>
                                            <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col items-center justify-center backdrop-blur-sm">
                                                <div className="p-4 bg-white/10 rounded-full mb-2">
                                                    <Camera size={32} className="text-secondary" />
                                                </div>
                                                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white">Actualizar Foto</span>
                                            </div>
                                            <button 
                                                type="button"
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPhotoPreview(null); setFormData(prev => ({...prev, foto_base64: null, foto_url: null})); }}
                                                className="absolute top-4 right-4 p-2 bg-red-500/20 hover:bg-red-500/80 rounded-xl text-white backdrop-blur-md border border-white/10 transition-all z-10"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <div className="relative">
                                                <div className="p-6 bg-white/5 rounded-full mb-4 group-hover:bg-secondary/10 group-hover:scale-110 transition-all duration-500">
                                                    <Camera size={40} className="text-white/20 group-hover:text-secondary" />
                                                </div>
                                                <div className="absolute -bottom-1 -right-1 bg-secondary p-1.5 rounded-lg shadow-lg">
                                                    <Plus size={14} className="text-white font-black" />
                                                </div>
                                            </div>
                                            <div className="flex flex-col items-center px-6 text-center">
                                                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40 group-hover:text-white transition-colors">Foto de la Mascota</span>
                                                <p className="text-[9px] text-white/10 uppercase mt-2 font-medium group-hover:text-white/30">Toca para abrir cámara o galería</p>
                                            </div>
                                        </>
                                    )}
                                </label>
                           </div>

                     <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                               <label className="text-[10px] font-bold uppercase tracking-widest text-secondary">Nombre de la Mascota</label>
                               <input type="text" value={formData.nombre} onChange={e => setFormData({...formData, nombre: e.target.value})} className="input-field bg-slate-800/80" placeholder="Ej. Max" />
                          </div>
                          <div className="space-y-2">
                               <label className="text-[10px] font-bold uppercase tracking-widest text-secondary">Especie</label>
                               <select 
                                 value={formData.especie_id} 
                                 onChange={e => setFormData({...formData, especie_id: e.target.value, raza_id: ''})} 
                                 className="input-field bg-slate-800/80"
                               >
                                   <option value="" disabled>Seleccionar...</option>
                                   {especies.map(es => <option key={es.id} value={es.id} className="bg-slate-900">{es.nombre}</option>)}
                               </select>
                          </div>
                          <div className="space-y-2">
                               <label className="text-[10px] font-bold uppercase tracking-widest text-secondary text-right justify-end flex gap-1">
                                 <ChevronRight size={10} className="text-secondary" /> Género
                               </label>
                               <div className="grid grid-cols-2 gap-2">
                                   <button 
                                     type="button"
                                     onClick={() => setFormData({...formData, genero: 'Macho'})}
                                     className={`py-2.5 rounded-xl border font-bold text-xs transition-all ${formData.genero === 'Macho' ? 'bg-primary border-primary shadow-lg shadow-primary/20 text-white' : 'glass border-white/5 text-white/30'}`}
                                   >MACHO</button>
                                   <button 
                                     type="button"
                                     onClick={() => setFormData({...formData, genero: 'Hembra'})}
                                     className={`py-2.5 rounded-xl border font-bold text-xs transition-all ${formData.genero === 'Hembra' ? 'bg-pink-600 border-pink-600 shadow-lg shadow-pink-600/20 text-white' : 'glass border-white/5 text-white/30'}`}
                                   >HEMBRA</button>
                               </div>
                          </div>
                          <div className="space-y-2">
                               <label className="text-[10px] font-bold uppercase tracking-widest text-secondary">Raza</label>
                               <select 
                                 value={formData.raza_id} 
                                 onChange={e => setFormData({...formData, raza_id: e.target.value})} 
                                 className="input-field bg-slate-800/80"
                               >
                                   <option value="">Raza Mixta / Otra</option>
                                   {filteredRazas.map(r => <option key={r.id} value={r.id} className="bg-slate-900">{r.nombre}</option>)}
                               </select>
                               {formData.raza_id === '' && (
                                   <input 
                                     type="text" 
                                     value={formData.raza_custom} 
                                     onChange={e => setFormData({...formData, raza_custom: e.target.value})} 
                                     className="input-field mt-2 bg-slate-800/80 text-xs py-2" 
                                     placeholder="Describe la raza si es mixta..." 
                                   />
                               )}
                          </div>
                     </div>

                     <div className="space-y-2">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-secondary flex items-center gap-2">
                              <Calendar size={12} /> Fecha Nacimiento
                          </label>
                          <input type="date" value={formData.fecha_nacimiento} onChange={e => setFormData({...formData, fecha_nacimiento: e.target.value})} className="input-field bg-slate-800/80 [color-scheme:dark]" />
                     </div>

                     <div className="space-y-2">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-secondary flex items-center gap-2">
                              <Scale size={12} /> Peso (LBS)
                          </label>
                          <input type="number" value={formData.peso} onChange={e => setFormData({...formData, peso: e.target.value})} className="input-field bg-slate-800/80" placeholder="0.0" />
                     </div>

                     <div className="md:col-span-3 space-y-2">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-secondary flex items-center gap-2">
                              <Scissors size={12} /> Notas de Grooming
                          </label>
                          <textarea value={formData.notas} onChange={e => setFormData({...formData, notas: e.target.value})} className="input-field h-24 bg-slate-800/80 resize-none font-medium text-sm" placeholder="Alergias, temperamento o requerimientos especiales..."></textarea>
                     </div>
                </form>
            </Modal>

            {/* Modal de Historial Clínico - Timeline */}
            <Modal
                show={showHistory}
                onClose={() => setShowHistory(false)}
                title={selectedMascota ? `Expediente: ${selectedMascota.nombre}` : 'Historial de Mascota'}
                size="lg"
            >
                <div className="space-y-8 max-h-[600px] overflow-y-auto pr-4 custom-scrollbar pb-8">
                    {loadingHistory ? (
                        <div className="py-20 text-center animate-pulse text-white/20">Sincronizando expediente veterinario...</div>
                    ) : (
                        <>
                            {/* Agenda Pendiente */}
                            {historyData.agenda_pendiente.length > 0 && (
                                <section className="space-y-4">
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-secondary flex items-center gap-2">
                                        <Clock size={12} /> Próximas Citas y Pendientes
                                    </h4>
                                    <div className="space-y-3">
                                        {historyData.agenda_pendiente.map((cita, idx) => (
                                            <div key={idx} className="glass p-4 rounded-2xl border-l-4 border-l-secondary relative overflow-hidden group">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <div className="text-sm font-black text-white/90">{new Date(cita.fecha_hora).toLocaleDateString('es-PR', { day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' })}</div>
                                                        <div className="text-[10px] text-white/40 font-bold uppercase tracking-widest mt-1">Con {cita.empleado_nombre}</div>
                                                    </div>
                                                    <span className="bg-secondary/10 text-secondary text-[10px] font-black px-2 py-1 rounded-lg uppercase">{cita.estado}</span>
                                                </div>
                                                {cita.notas && <p className="text-xs text-white/30 italic mt-2 border-t border-white/5 pt-2 italic">"{cita.notas}"</p>}
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* Timeline de Servicios Realizados */}
                            <section className="space-y-4">
                                <h4 className="text-[10px] font-black uppercase tracking-widest text-primary-light flex items-center gap-2">
                                    <CheckCircle2 size={12} /> Línea del Tiempo de Servicios
                                </h4>
                                
                                <div className="relative pl-8 space-y-8">
                                    {/* Linea vertical */}
                                    <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-gradient-to-b from-primary-light/50 to-transparent"></div>
                                    
                                    {historyData.servicios_realizados.length > 0 ? historyData.servicios_realizados.map((item, idx) => (
                                        <div key={idx} className="relative group">
                                            {/* Punto en la linea */}
                                            <div className="absolute -left-[25px] top-1.5 w-3 h-3 rounded-full bg-primary-light ring-4 ring-primary-light/20 shadow-lg shadow-primary-light/40 group-hover:scale-125 transition-transform"></div>
                                            
                                            <div className="flex justify-between items-start">
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] font-black text-white/20 uppercase tracking-widest">
                                                        {new Date(item.fecha).toLocaleDateString('es-PR', { day: '2-digit', month: 'long', year: 'numeric' })}
                                                    </span>
                                                    <h5 className="text-lg font-black text-white/90 group-hover:text-primary-light transition-colors">{item.servicio}</h5>
                                                    <div className="flex items-center gap-3 mt-1">
                                                        <span className="text-[10px] font-bold text-white/30 flex items-center gap-1">
                                                            <User size={10} /> {item.atendido_por}
                                                        </span>
                                                        <span className="text-[10px] font-bold text-white/30 flex items-center gap-1">
                                                            <History size={10} /> Factura #{item.factura}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-xl font-black text-white">$ {item.costo}</div>
                                                    <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-lg uppercase tracking-widest">Cobrado</span>
                                                </div>
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="py-10 text-white/10 italic text-sm">No se registran servicios previos facturados.</div>
                                    )}
                                </div>
                            </section>
                        </>
                    )}
                </div>
                <div className="mt-6 pt-6 border-t border-white/5 flex justify-end">
                    <button onClick={() => setShowHistory(false)} className="glass px-8 py-3 rounded-2xl font-bold text-xs uppercase tracking-widest hover:bg-white/10 transition-all">
                        Cerrar Expediente
                    </button>
                </div>
            </Modal>

            {/* Modal Premium de Vista Previa PDF */}
            <Modal
                show={showPreview}
                onClose={() => setShowPreview(false)}
                title="Vista Previa de Catálogo (PDF)"
                size="lg"
            >
                <div className="w-full h-[70vh] rounded-2xl overflow-hidden glass border border-white/5 shadow-2xl relative">
                    <iframe 
                        src={`/backend/api/reportes/mascotas_pdf.php?token=${localStorage.getItem('token')}`}
                        className="w-full h-full border-0 hidden md:block"
                        title="PDF Preview"
                    ></iframe>

                    {/* Fallback Tablet */}
                    <div className="md:hidden flex flex-col items-center justify-center h-full p-10 text-center">
                        <Dog size={48} className="text-secondary mb-4" />
                        <h3 className="text-lg font-black text-white mb-2">Visor de Catálogo</h3>
                        <p className="text-white/40 mb-6 text-xs font-medium">Para una mejor experiencia en su tablet, el catálogo debe abrirse de forma externa.</p>
                        <button 
                            onClick={() => window.open(`/backend/api/reportes/mascotas_pdf.php?token=${localStorage.getItem('token')}`, '_blank')}
                            className="premium-btn-secondary px-8 py-3 flex items-center gap-2 text-xs"
                        >
                            <Eye size={16} /> VER CATÁLOGO AHORA
                        </button>
                    </div>
                </div>
                <div className="mt-6 flex justify-between items-center bg-white/5 p-4 rounded-2xl border border-white/5">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-secondary/20 rounded-xl">
                            <FileText size={18} className="text-secondary" />
                        </div>
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-widest text-white/40">Formato Estándar</p>
                            <p className="text-xs font-bold text-white">Reporte_Mascotas_General.pdf</p>
                        </div>
                    </div>
                    <div className="flex gap-4">
                        <button 
                            onClick={() => setShowPreview(false)}
                            className="px-6 py-2 rounded-xl glass hover:bg-white/5 text-[10px] font-black uppercase tracking-widest transition-all"
                        >
                            Cerrar
                        </button>
                        <a 
                            href={`/backend/api/reportes/mascotas_pdf.php?token=${localStorage.getItem('token')}`}
                            target="_blank"
                            rel="noreferrer"
                            className="premium-btn-primary px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-primary/20 flex items-center gap-2"
                        >
                            <ArrowRight size={14} /> Abrir Externo
                        </a>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Mascotas;
